/** @type {boolean | null} */
let digitalFeeStoreEnabledCache = null;
/** @type {Promise<boolean> | null} */
let digitalFeeStoreEnabledPromise = null;

/**
 * Whether the store has digital delivery fees enabled (store-level check via downloadprovidercode=*).
 * @returns {Promise<boolean>}
 */
function isDigitalDeliveryNoticeStoreEnabled() {
  if (digitalFeeStoreEnabledCache !== null) {
    return Promise.resolve(digitalFeeStoreEnabledCache);
  }
  if (digitalFeeStoreEnabledPromise) {
    return digitalFeeStoreEnabledPromise;
  }

  const config = window.digital_product_config;
  if (!config?.api_url || !config?.store_number) {
    digitalFeeStoreEnabledCache = false;
    return Promise.resolve(false);
  }

  const requestUrl = `${config.api_url}?storeNumber=${config.store_number}&downloadprovidercode=*`;
  digitalFeeStoreEnabledPromise = fetch(requestUrl)
    .then((res) => {
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    })
    .then((data) => {
      const enabled =
        Array.isArray(data) &&
        data.some((entry) => entry?.storeEnabled === true);
      digitalFeeStoreEnabledCache = enabled;
      return enabled;
    })
    .catch((e) => {
      console.error("[digital-fee] Store enabled check failed:", e);
      digitalFeeStoreEnabledCache = false;
      return false;
    })
    .finally(() => {
      digitalFeeStoreEnabledPromise = null;
    });

  return digitalFeeStoreEnabledPromise;
}

window.isDigitalDeliveryNoticeStoreEnabled = isDigitalDeliveryNoticeStoreEnabled;

/**
 * Checks if a digital fee should be applied for given publisher codes
 * @param {string | string[]} publisherCodes - Publisher code(s) to check
 * @returns {Promise<Array<{code: string, isApplied: boolean, storeEnabled?: boolean}>>}
 */
function queryDigitalDeliveryFeesApi(publisherCodes) {
  if (
    window.digital_product_config == undefined ||
    window.digital_product_config?.api_url == undefined ||
    window.digital_product_config?.store_number == undefined
  ) {
    throw Error("Digital fee config does not exist");
  }

  const api_url = window.digital_product_config.api_url;
  const store_number = window.digital_product_config.store_number;

  let uniqueCodes;
  if (typeof publisherCodes === "string") {
    uniqueCodes = [
      ...new Set(
        publisherCodes
          .split("|")
          .map((s) => s.trim())
          .filter(Boolean)
      ),
    ];
  } else {
    uniqueCodes = [
      ...new Set(
        publisherCodes.map((c) => String(c).trim()).filter(Boolean)
      ),
    ];
  }
  const queryValue = uniqueCodes.join("|");

  if (queryValue != "") {
    const requestUrl = `${api_url}?storeNumber=${store_number}&downloadprovidercode=${queryValue}`;
    return fetch(requestUrl)
      .then((res) => res.json())
      .then((data) => {
        console.log("[digital-fee] API request:", requestUrl);
        console.log("[digital-fee] API raw response:", data);
        const mapped = data.map((entry) => {
          return {
            code: entry.code,
            isApplied: entry.isApplied ?? false,
            storeEnabled: entry.storeEnabled ?? false,
          };
        });
        console.log("[digital-fee] API mapped (code + isApplied):", mapped);
        return mapped;
      })
      .catch((e) => {
        console.error("[digital-fee] API request failed:", e);
        return [];
      });
  } else {
    return Promise.resolve([]);
  }
}

/**
 * Hide/remove digital fee tooltips on digital variant rows only.
 * Print used-material tooltips use .tooltip-trigger without .notice-tooltip.
 */
function suppressDigitalFeeTooltipsOnDigitalVariants() {
  document
    .querySelectorAll('input.purchase-checkbox[data-is-digital="true"]')
    .forEach((input) => {
      const row = input.closest(".purchase-type, .accordion-variant-option");
      row?.querySelectorAll(".notice-tooltip[data-variant-id]").forEach((el) => {
        el.remove();
      });
    });
}

// Digital fee tooltip visibility management
async function updateDigitalFeeTooltips() {
  const storeEnabled = await isDigitalDeliveryNoticeStoreEnabled();
  if (!storeEnabled) {
    suppressDigitalFeeTooltipsOnDigitalVariants();
    return;
  }

  const checkboxContainers = document.querySelectorAll(
    ".purchase-checkbox-container"
  );

  if (checkboxContainers.length === 0) {
    return;
  }

  const publisherCodesToCheck = [];
  checkboxContainers.forEach((container) => {
    const input = container.querySelector("input.purchase-checkbox");

    if (!input) return;

    const isDigital = input.dataset.isDigital === "true";
    const publisherCode = input.dataset.publisherCode;

    if (
      isDigital &&
      publisherCode &&
      publisherCode !== "null" &&
      publisherCode.trim() !== ""
    ) {
      publisherCodesToCheck.push(publisherCode);
    }
  });

  if (publisherCodesToCheck.length === 0) {
    return;
  }

  queryDigitalDeliveryFeesApi(publisherCodesToCheck).then((results) => {
    console.log(
      "[digital-fee] Tooltip visibility pass; unhide when isApplied is true:",
      results
    );
    if (!Array.isArray(results)) {
      return;
    }

    // Publisher codes that qualify for showing the fee notice.
    const appliedCodes = new Set();
    results.forEach((result) => {
      if (result.isApplied ?? false) {
        appliedCodes.add(result.code);
      }
    });

    // One Map entry per publisher code used to overwrite all but the last variant — many
    // rows share a code (e.g. SAGE). Unhide every matching tooltip by walking checkboxes.
    // Support both CMRP rows (.purchase-type) and PDP rows (.accordion-variant-option).
    checkboxContainers.forEach((container) => {
      const input = container.querySelector("input.purchase-checkbox");
      if (!input) return;

      const isDigital = input.dataset.isDigital === "true";
      const publisherCode = input.dataset.publisherCode;

      if (
        !isDigital ||
        !publisherCode ||
        publisherCode === "null" ||
        !publisherCode.trim() ||
        !appliedCodes.has(publisherCode)
      ) {
        return;
      }

      const row = container.closest(".purchase-type, .accordion-variant-option");
      let tooltip = row?.querySelector(".notice-tooltip");
      if (!tooltip) {
        const variantId = input.dataset.variantId;
        if (variantId) {
          tooltip = container
            .closest("label, .accordion-variant-option, .purchase-type")
            ?.querySelector(`.notice-tooltip[data-variant-id="${variantId}"]`);
        }
      }
      if (tooltip) {
        tooltip.classList.remove("hidden");
      }
    });
  });
}

// Run tooltip update when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", updateDigitalFeeTooltips);
} else {
  updateDigitalFeeTooltips();
}

document.addEventListener("product-display:updated", () => {
  updateDigitalFeeTooltips();
});
